import './QuizQuestion.scss';

import * as React from 'react';

import { translate } from '~l10n/client';
import cx from 'classnames';
import { useAtom, useSetAtom } from 'jotai';
import { useDispatch, useSelector } from 'react-redux';

import { IconButton } from '~shared/components/buttons';
import { IconCheckmark } from '~shared/components/icons/large';
import {
  IconArrow,
  IconCopy,
  IconPencil,
  IconTrash,
} from '~shared/components/icons/standard';
import { InvalidQuizModal } from '~teacher/components/InvalidQuizModal/InvalidQuizModal';
import {
  questionIdsToDelete,
  selectedQuestionIds,
} from '~teacher/components/QuizEditor/atoms/selectedQuestionAtom';
import { useQuizQuestionOrderUrl } from '~teacher/components/QuizEditor/contexts/QuizQuestionOrderUrlProvider';
import { useQuizQuestionUrl } from '~teacher/components/QuizEditor/contexts/QuizQuestionUrlProvider';
import { isTempQuestionId } from '~teacher/components/QuizEditor/contexts/UseBulkDeleteQuestionProvider';
import { deleteTypeAtom } from '~teacher/components/QuizEditor/QuizEditor';
import { QuizEditorIsUploadingImageContext } from '~teacher/components/QuizEditor/QuizEditorIsUploadingImage';
import { useSelectQuestion } from '~teacher/components/QuizQuestion/helpers/useSelectQuestion';
import { ValidationError } from '~teacher/models/errors';
import { proCheck } from '~teacher/util/proCheck';

import {
  changeQuestionOrder,
  editQuestion,
  handleUnexpectedError,
  saveQuestion,
} from '../../slices/quiz/quizAppThunks';
import {
  makeSelectIsControlsDisabled,
  selectEditQuestion,
  selectIsEditing,
} from '../../slices/quiz/quizSelectors';
import { copyQuestion } from '../../slices/quiz/quizSlice';
import QuestionType from './QuizQuestionType';

function useHandleEdit(
  questionId: string,
  setInvalidations: React.Dispatch<React.SetStateAction<unknown[]>>
) {
  const dispatch = useDispatch();
  const quizQuestionUrl = useQuizQuestionUrl();
  const selectQuestion = useSetAtom(selectedQuestionIds.addAtom);
  const unselectQuestion = useSetAtom(selectedQuestionIds.removeAtom);
  const [selectedQuestions] = useAtom(selectedQuestionIds.valueAtom);

  return async () => {
    try {
      await dispatch(
        editQuestion({
          questionId: questionId,
          quizQuestionUrl: quizQuestionUrl,
          unselectQuestion: unselectQuestion,
          selectedQuestions: selectedQuestions,
          selectQuestion: selectQuestion,
        })
      );
    } catch (error) {
      if (error instanceof ValidationError) {
        setInvalidations(error.invalidations);
      } else {
        dispatch(handleUnexpectedError(error));
      }
    }
  };
}

function useHandleSave(
  setInvalidations: React.Dispatch<React.SetStateAction<unknown[]>>
) {
  const quizQuestionUrl = useQuizQuestionUrl();
  const dispatch = useDispatch();
  const selectQuestion = useSetAtom(selectedQuestionIds.addAtom);

  const unselectQuestion = useSetAtom(selectedQuestionIds.removeAtom);
  const [selectedQuestions] = useAtom(selectedQuestionIds.valueAtom);
  return async () => {
    try {
      await dispatch(
        saveQuestion({
          quizQuestionUrl: quizQuestionUrl,
          unselectQuestion: unselectQuestion,
          selectQuestion: selectQuestion,
          selectedQuestions: selectedQuestions,
        })
      );
    } catch (error) {
      if (error instanceof ValidationError) {
        setInvalidations(error.invalidations);
      } else {
        dispatch(handleUnexpectedError(error));
      }
    }
  };
}

function useHandleCopy(questionId: string) {
  const showAdvancedQuizEditor = proCheck.canShowAdvancedQuizEditor();
  const dispatch = useDispatch();
  return () => {
    dispatch(
      copyQuestion({
        questionId: questionId,
        showAdvancedQuizEditor: showAdvancedQuizEditor,
      })
    );
  };
}

function useHandleMoveQuestion({
  questionId,
  currentOrder,
  orderChange,
}: {
  // The id of the question to move. Will not move questions with temp id
  questionId: string;
  // What order is the question currently in?
  currentOrder: number;
  // How many orders should this item move? Use negative numbers to move up, and positive numbers to move down.
  orderChange: number;
}) {
  const quizQuestionOrderUrl = useQuizQuestionOrderUrl();
  const dispatch = useDispatch();
  return async () => {
    try {
      if (isTempQuestionId(questionId)) {
        return;
      }

      const newOrder = currentOrder + orderChange;

      await dispatch(
        changeQuestionOrder({
          questionId,
          order: newOrder,
          quizQuestionOrderUrl,
        })
      );
    } catch (e) {
      dispatch(handleUnexpectedError(e));
    }
  };
}

interface Props {
  quizId: number;
  isLocked?: boolean;
  order: number;
  questionId: string;
  disableMoveUp?: boolean;
  disableMoveDown?: boolean;
  hasCustomGradingWeight?: boolean;
}

function useHandleShowDeleteQuestionModal(questionId: string) {
  const [, replaceQuestionIds] = useAtom(questionIdsToDelete.replaceAtom);
  const [, setDeleteSource] = useAtom(deleteTypeAtom);

  return () => {
    setDeleteSource('single');
    replaceQuestionIds(new Set([questionId]));
  };
}

/**
 * Provides functionality common to all Question types, within the
 * Quiz Editor screen; saving, editing, reordering, duplicating, and
 * removing. Renders the appropriate fields/data based on the `type`
 * of the Question passed in.
 */
export default function QuizQuestion({
  isLocked,
  order,
  questionId,
  disableMoveUp = false,
  disableMoveDown = false,
  hasCustomGradingWeight = false,
}: Props) {
  const isEditing: boolean = useSelector(selectIsEditing);

  const isControlsDisabled: ReturnType<
    ReturnType<typeof makeSelectIsControlsDisabled>
  > = useSelector(makeSelectIsControlsDisabled());

  const { type: questionType } = useSelectQuestion({ questionId: questionId });

  const editQuestionState: ReturnType<typeof selectEditQuestion> =
    useSelector(selectEditQuestion);

  const isEditingThisQuestion = editQuestionState?.questionId == questionId;

  const [invalidations, setInvalidations] = React.useState<any[]>([]);

  const handleShowDeleteQuestionModal =
    useHandleShowDeleteQuestionModal(questionId);

  const handleEdit = useHandleEdit(questionId, setInvalidations);
  const handleSave = useHandleSave(setInvalidations);

  const handleOrderUp = useHandleMoveQuestion({
    questionId: questionId,
    currentOrder: order,
    orderChange: -1,
  });

  const handleOrderDown = useHandleMoveQuestion({
    questionId: questionId,
    currentOrder: order,
    orderChange: +1,
  });

  const handleCopy = useHandleCopy(questionId);
  const questionClass = cx(
    'flex items-start py-10 md:pl-2 md:pr-10 gap-4',
    'quiz-question',
    {
      'shadow-border-t': !isEditingThisQuestion,
      'px-8 -mx-8 sm:mx-0 bg-grey-50 sm:rounded-4 is-editing':
        isEditingThisQuestion,
    }
  );

  return (
    <div className={questionClass} data-quiz-question>
      <QuizEditorIsUploadingImageContext.Consumer>
        {({ isUploadingImages }) => (
          <>
            <QuestionType
              isLocked={isLocked}
              questionId={questionId}
              order={order}
              questionType={questionType}
              hasCustomGradingWeight={hasCustomGradingWeight}
            />
            {!isLocked ? (
              <div
                className={`flex-none flex flex-col justify-center ${
                  isEditingThisQuestion ? 'md:ml-8' : 'ml-4 md:ml-8'
                }`}
              >
                <IconButton
                  aria-label={
                    isEditingThisQuestion
                      ? translate('Save Question')
                      : translate('Edit Question')
                  }
                  appearance="primary"
                  disabled={isUploadingImages || isControlsDisabled}
                  onClick={isEditingThisQuestion ? handleSave : handleEdit}
                >
                  <RenderModeIcon isEditing={isEditingThisQuestion} />
                </IconButton>
                <div className="mt-4">
                  <IconButton
                    isRisky
                    withAltBackground={isEditingThisQuestion}
                    aria-label={translate('Delete Question')}
                    disabled={
                      isUploadingImages ||
                      (isEditing && !isEditingThisQuestion) ||
                      isControlsDisabled
                    }
                    onClick={handleShowDeleteQuestionModal}
                  >
                    <IconTrash />
                  </IconButton>
                </div>
                <div className={`mt-2 ${isEditing && 'invisible'}`}>
                  <IconButton
                    withAltBackground={isEditingThisQuestion}
                    aria-label={translate('Move Question Up')}
                    disabled={
                      isUploadingImages ||
                      isEditing ||
                      isControlsDisabled ||
                      disableMoveUp
                    }
                    onClick={handleOrderUp}
                  >
                    <IconArrow />
                  </IconButton>
                </div>
                <div className={`mt-2 ${isEditing && 'invisible'}`}>
                  <IconButton
                    withAltBackground={isEditingThisQuestion}
                    disabled={
                      isUploadingImages ||
                      isEditing ||
                      isControlsDisabled ||
                      disableMoveDown
                    }
                    aria-label={translate('Move Question Down')}
                    onClick={handleOrderDown}
                  >
                    <IconArrow direction="down" />
                  </IconButton>
                </div>
                <div className={`mt-2 ${isEditing && 'invisible'}`}>
                  <IconButton
                    withAltBackground={isEditingThisQuestion}
                    aria-label={translate('Copy Question')}
                    disabled={
                      isUploadingImages || isEditing || isControlsDisabled
                    }
                    onClick={handleCopy}
                  >
                    <IconCopy />
                  </IconButton>
                </div>
              </div>
            ) : null}
            {invalidations?.length > 0 && (
              <InvalidQuizModal
                invalidation={invalidations?.[0] ?? null}
                onClose={() => setInvalidations([])}
              />
            )}
          </>
        )}
      </QuizEditorIsUploadingImageContext.Consumer>
    </div>
  );
}

function RenderModeIcon({ isEditing }: { isEditing: boolean }) {
  return isEditing ? <IconCheckmark /> : <IconPencil />;
}
